"use client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import Image from "next/image";
import React from "react";

type TeamMember = {
  id: string;
  name: string;
  role: string;
  image: string;
  social?: {
    instagram?: string;
  };
};

const teamMembers: TeamMember[] = [
  {
    id: "1",
    name: "Praveen Singh",
    role: "Designer",
    image: "/Praveen.webp",
    social: {
      instagram: "o_its_praveen",
    },
  },
  {
    id: "2",
    name: "Sulabh Singh",
    role: "Photographer",
    image: "/Sulabh.webp",
    social: {
      instagram: "sulabh_photography",
    },
  },
  {
    id: "3",
    name: "Vinod Kumar Singh",
    role: "Drone Pilot",
    image: "/Vinod.webp",
    social: {
      instagram: "er_vinod_yt_",
    },
  },
  {
    id: "4",
    name: "Akash",
    role: "Videographer",
    image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg",
    social: {
      instagram: "akash_photography",
    },
  },
];

export default function Team() {
  const sliderRef = React.useRef<HTMLDivElement>(null);

  const scrollByCards = (dir: "prev" | "next") => {
    const el = sliderRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-team-card]");
    const cardWidth = card ? card.offsetWidth : el.clientWidth;
    const amount = dir === "next" ? cardWidth + 16 : -(cardWidth + 16);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section
      id="team"
      className="relative isolate w-full px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:px-8"
    >
      {/* Fancy gradient background - continues from about bottom right and bottom left */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Base gradient - starts with about's ending colors at top right and top left */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 100% 80% at top left, oklch(0.6 0.118 184.704 / 0.08), transparent 60%),
              radial-gradient(ellipse 100% 80% at top center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at top right, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at left center, oklch(0.6 0.118 184.704 / 0.08), transparent 60%),
              radial-gradient(ellipse 100% 80% at right center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom left, oklch(0.6 0.118 184.704 / 0.08), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              linear-gradient(to bottom left, oklch(0.769 0.188 70.08 / 0.1) 0%, transparent 40%, oklch(0.6 0.118 184.704 / 0.08) 100%),
              oklch(1 0 0)
            `,
          }}
        />

        {/* Animated floating orbs */}
        <div className="bg-chart-4/20 absolute top-20 -right-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
        <div className="bg-chart-1/20 absolute bottom-20 -left-32 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
        <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

        {/* Subtle mesh gradient overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(to bottom left, transparent 0%, oklch(0.828 0.189 84.429 / 0.05) 30%, oklch(0.646 0.222 41.116 / 0.05) 70%, transparent 100%)",
          }}
        />
      </div>
      <div className="container mx-auto">
        <div className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mb-8 motion-safe:duration-700">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Meet Our Team</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Meet the creative minds behind every shot.
          </p>
        </div>
        <div className="relative">
          <div className="-mx-2 flex items-center justify-between gap-2 sm:gap-4">
            <Button
              variant="outline"
              size="icon"
              className="mr-1 hidden shrink-0 sm:mr-2 md:inline-flex"
              aria-label="Previous team members"
              onClick={() => scrollByCards("prev")}
            >
              <ChevronLeft className="size-4 sm:size-5" />
            </Button>
            <div
              ref={sliderRef}
              className="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-6 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-4 lg:gap-6 [&::-webkit-scrollbar]:hidden"
              aria-label="Team carousel"
              role="region"
            >
              {teamMembers.map((member, idx) => (
                <Card
                  key={member.id}
                  data-team-card
                  className={
                    "group motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 relative min-w-[85%] snap-start overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md motion-safe:duration-700 sm:min-w-[55%] lg:min-w-[33.333%] " +
                    [" ", " motion-safe:delay-75", " motion-safe:delay-150"][idx % 3]
                  }
                  style={{
                    background: `
                      linear-gradient(to bottom right, oklch(0.769 0.188 70.08 / 0.05), oklch(0.828 0.189 84.429 / 0.03)),
                      oklch(1 0 0)
                    `,
                  }}
                >
                  <CardHeader className="p-0">
                    <div className="relative mb-3 aspect-square overflow-hidden rounded-t-xl sm:mb-4">
                      {/* Loading skeleton */}
                      <div className="bg-muted absolute inset-0 animate-pulse">
                        <div className="bg-muted-foreground/20 absolute inset-0" />
                      </div>
                      <Image
                        src={member.image}
                        alt={member.name}
                        width={400}
                        height={400}
                        loading="lazy"
                        quality={85}
                        placeholder="blur"
                        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                        className="relative z-10 size-full object-cover transition-transform duration-500 group-hover:scale-110"
                        sizes="(max-width: 640px) 85vw, (max-width: 1024px) 55vw, 33vw"
                      />
                      <div className="pointer-events-none absolute inset-0 z-20 bg-linear-to-t from-black/25 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-4">
                    <div className="flex items-start justify-between gap-2 sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="group-hover:text-primary text-base transition-colors duration-300 sm:text-lg">
                          {member.name}
                        </CardTitle>
                        <CardDescription className="text-primary text-xs font-medium sm:text-sm">
                          {member.role}
                        </CardDescription>
                      </div>
                      {member.social?.instagram && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={
                                  member.social.instagram.startsWith("http")
                                    ? member.social.instagram
                                    : `https://www.instagram.com/${member.social.instagram.replace("@", "")}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`${member.name}'s Instagram`}
                                className="bg-background/70 focus-visible:ring-ring inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] shadow-xs backdrop-blur transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:gap-2 sm:px-2.5 sm:py-1.5 sm:text-xs"
                              >
                                <Instagram className="size-3 text-[#E1306C] sm:size-4" />
                                <span className="hidden sm:inline">Instagram</span>
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              @{member.social.instagram.replace("@", "")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="ml-1 hidden shrink-0 sm:ml-2 md:inline-flex"
              aria-label="Next team members"
              onClick={() => scrollByCards("next")}
            >
              <ChevronRight className="size-4 sm:size-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
